import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";
import { useAuthToken } from "../data/redux/useAuthToken";

// ------------------
// Interfaces
// ------------------

export interface Product {
  product_id: number;
  product_name: string;
  description: string;
}

export interface Client {
  company_license: string;
  company_name: string;
  email: string;
  server_name: string;
  company_batch: string;
  contact_number: number;
  product_id: number;
  modules: string[];
  status: string;
  client_id: string;
  product: Product;
}

export interface Module {
  module_name: string;
  description: string;
  product_id: number;
  module_id: number;
  product: Product;
  created_at: string;
  updated_at: string;
}

// ------------------
// API Endpoints
// ------------------

const CLIENTS_API = `${API_BASE_PATH}/clients/`;
const MODULES_API = `${API_BASE_PATH}/productmodules/`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ------------------
// Fetch Functions
// ------------------

export const fetchClients = async (
  _token: string | null
): Promise<Client[]> => {
  try {
    const response = await axios.get<Client[]>(CLIENTS_API, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });

    const clientsWithParsedModules = response.data.map((client) => ({
      ...client,
      modules: client.modules || [],
    }));

    return clientsWithParsedModules;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

export const fetchModules = async (
  _token: string | null
): Promise<Module[]> => {
  try {
    const response = await axios.get<Module[]>(MODULES_API, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });

    return response.data;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// ------------------
// Helper: Extract Unique Products from Clients
// ------------------

export const getUniqueProductsFromClients = (clients: Client[]): Product[] => {
  const seen = new Set<number>();
  const uniqueProducts: Product[] = [];

  clients.forEach((client) => {
    const { product } = client;
    if (product && !seen.has(product.product_id)) {
      seen.add(product.product_id);
      uniqueProducts.push(product);
    }
  });

  return uniqueProducts;
};

// ------------------
// Helper: Parse Module ID Strings
// ------------------

export const parseModuleIds = (rawModules: string[]): number[] => {
  return rawModules
    .flatMap((str) => str.split(","))
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !isNaN(id));
};

// ------------------
// Enquiry API
// ------------------

const ENQUIRY_API = `${API_BASE_PATH}/enquiries/`;

interface EnquiryPayload {
  company_name: string;
  company_license: string;
  email: string | null;
  product_id: string | number;
  module_id: string | number;
  enquiry_type: string;
  requirement: string;
  status?: string;
  taken_by: string;
  category?: number | null; // Changed to number | null
}

export const submitNewEnquiry = async (
  payload: EnquiryPayload,
  attachments: File[] = [],
  onProgress?: (progress: number) => void,
  _token?: string | null
): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("company_name", payload.company_name);
    formData.append("company_license", payload.company_license);
    if (payload.email) formData.append("email", payload.email);
    if (payload.category !== null && payload.category !== undefined) {
      formData.append("category", String(payload.category));
    }
    formData.append("product_id", String(payload.product_id));
    formData.append("module_id", String(payload.module_id));
    formData.append("enquiry_type", payload.enquiry_type);
    formData.append("requirement", payload.requirement);
    formData.append("status", payload.status || "Pending");
    formData.append("taken_by", payload.taken_by);
    attachments.forEach((file) => formData.append("upload_file", file));
    await axios.post(ENQUIRY_API, formData, {
      headers: {
        Accept: "application/json",
        authorization: "Bearer " + _token,
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        if (onProgress) onProgress(percent);
      },
    });
    toast.success("Enquiry submitted successfully");
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// ------------------
// Fetch Enquiries for Table
// ------------------

export interface EnquiryResponse {
  enquiry_id: string;
  company_name: string;
  company_license: string | null;
  email: string | null;
  product_id: number | null;
  module_id: number | null;
  enquiry_type: string;
  requirement: string;
  note: string | null;
  status: string | null;
  rejection_reason: string | null;
  taken_by: string | null;
  upload_file: string | null;
  category: number | null; // Changed to number | null
  date: string;
}

export interface TableEnquiryRow {
  enquiry_id: string;
  company_name: string;
  company_license: string | null;
  email: string | null;
  product_id: number | null;
  module_id: number | null;
  enquiry_type: string;
  requirement: string;
  note: string | null;
  status: string | null;
  rejection_reason: string | null;
  taken_by: string | null;
  date: string;
  upload_file: string;
  category?: number | null; // Changed to number | null
}

const ENQUIRY_API_STATUS = `${API_BASE_PATH}/enquiries/`;

export const fetchEnquiriesForTable = async (
  _token: string | null
): Promise<TableEnquiryRow[]> => {
  try {
    const response = await axios.get<EnquiryResponse[]>(ENQUIRY_API_STATUS, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    return response.data.map((item) => ({
      enquiry_id: item.enquiry_id,
      company_name: item.company_name.toUpperCase(),
      company_license: item.company_license ?? "N/A",
      date: item.date,
      status: item.status ?? "Pending",
      enquiry_type: item.enquiry_type ?? "N/A",
      email: item.email ?? "N/A",
      product_id: item.product_id ?? null,
      module_id: item.module_id ?? null,
      requirement: item.requirement ?? "",
      note: item.note ?? "",
      rejection_reason: item.rejection_reason ?? "",
      taken_by: item.taken_by ?? "",
      upload_file: item.upload_file ?? "",
      category: item.category ?? null, // Now number | null
      fullData: item,
    }));
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// ------------------
// Update Enquiry
// ------------------

interface UpdateEnquiryPayload {
  requirement: string;
  enquiry_type: string;
  category?: number | null; // Changed to number | null
}

export const updateEnquiry = async (
  enquiryId: string,
  payload: UpdateEnquiryPayload,
  attachments: File[] = [],
  _token?: string | null
): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("requirement", payload.requirement);
    formData.append("enquiry_type", payload.enquiry_type);
    if (payload.category !== null && payload.category !== undefined) {
      formData.append("category", String(payload.category));
    }
    attachments.forEach((file) => formData.append("newfile", file));

    await axios.put(`${ENQUIRY_API}${enquiryId}`, formData, {
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
        authorization: "Bearer " + _token,
      },
    });

    toast.success("Enquiry updated successfully");
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};

// ------------------
// Delete Enquiry
// ------------------

export const deleteEnquiry = async (
  enquiryId: string,
  _token?: string | null
): Promise<void> => {
  try {
    await axios.delete(`${ENQUIRY_API}${enquiryId}`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });

    toast.success("Enquiry deleted successfully");
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    throw error;
  }
};